import { createFileRoute } from "@tanstack/react-router";
import { Library } from "lucide-react";
import { SETTINGS_HEADER_CLASS } from "@/components/ui/setting-row";
import BrandsCollection from "@/components/brands/brands-collection";
import CategoriesCollection from "@/components/categories/categories-collection";
import DocumentsTypesCollection from "@/components/documents_types/documents_types_collection";
import EmissionPointsCollection from "@/components/emission_points/emission_points_collection";
import TagsCollection from "@/components/tags/tags-collection";
import {
  CollectionConfig,
  createCollectionWrapper,
} from "@/components/ui/collection-card/cards-collection-wrapper";
import { api } from "@/convex/_generated/api";
import { usePermissions } from "@/hooks/usePermissions";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";

export const Route = createFileRoute(
  "/_authenticated/settings/_layout/catalogs",
)({
  component: CollectionsCatalogsPage,
});

const collections: CollectionConfig[] = [
  {
    title: "Marcas",
    description: "Esta es una colección de marcas.",
    emptyMessage: "No hay marcas disponibles.",
    Component: BrandsCollection,
    module: "brands",
    query: api.companies.configuration.brands.queries.getProductsBrands,
    loadingLength: 4,
  },
  {
    title: "Categorías",
    description: "Esta es una colección de categorías.",
    emptyMessage: "No hay categorías disponibles.",
    Component: CategoriesCollection,
    module: "categories",
    query: api.companies.configuration.categories.queries.getProductCategories,
    loadingLength: 10,
  },
  {
    title: "Documentos",
    description: "Esta es una colección de documentos.",
    emptyMessage: "No hay documentos disponibles.",
    Component: DocumentsTypesCollection,
    module: "documents_types",
    query:
      api.companies.configuration.documents_types.queries.getDocumentsTypes,
    loadingLength: 10,
  },
  {
    title: "Puntos de emisión",
    description: "Puntos de emisión relacionado con la emisión de documentos.",
    emptyMessage: "No hay puntos de emisión disponibles.",
    Component: EmissionPointsCollection,
    module: "emission_points",
    query:
      api.companies.configuration.emission_points.queries.getEmissionPoints,
    loadingLength: 10,
  },
  {
    title: "Etiquetas",
    description: "Esta es una colección de etiquetas.",
    emptyMessage: "No hay etiquetas disponibles.",
    Component: TagsCollection,
    module: "tags",
    query: api.companies.tags.queries.getTagsByCompany,
    loadingLength: 10,
  },
];

function CollectionsCatalogsPage() {
  // El guard pide permiso de lectura sobre cada módulo que la página
  // efectivamente muestra (`brands`, `categories`, `documents_types`,
  // `emission_points`, `tags`). Antes se pedía `collection_records`,
  // pero ese módulo es interno de los registros dinámicos de cada
  // colección propia y no se concede automáticamente a los roles que
  // solo necesitan ver catálogos. Resultado: la página rebotaba a
  // NoAccess (que mostraba "Registros de colecciones" como nombre del
  // módulo) sin entrar nunca al contenido real. El permiso se chequea
  // acá a nivel de página y abajo, dentro del componente, con `can` se
  // filtra cuáles cards se pintan; ambos lados usan la misma fuente
  // para mantener una sola decisión.
  return (
    <RouteGuardPermissions
      permissions={[
        { module: "brands", actions: ["read"] },
        { module: "categories", actions: ["read"] },
        { module: "documents_types", actions: ["read"] },
        { module: "emission_points", actions: ["read"] },
        { module: "tags", actions: ["read"] },
      ]}
    >
      <CollectionsCatalogsContent />
    </RouteGuardPermissions>
  );
}

function CollectionsCatalogsContent() {
  const { can } = usePermissions();
  const visibleCollections = collections.filter((c) => can("read", c.module));

  return (
    // Formato de las páginas de ajustes (Empresa, Seguridad, Notificaciones).
    <div className="flex min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2 pb-6">
        <div className={SETTINGS_HEADER_CLASS}>
          <div className="flex items-center gap-2.5">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-background">
              <Library className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Catálogos</p>
              <p className="text-[11px] text-muted-foreground">
                Listas que se eligen al cargar productos, documentos y registros.
              </p>
            </div>
          </div>
        </div>
        {visibleCollections.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            No tienes catálogos visibles con tu rol actual.
          </p>
        ) : (
          visibleCollections.map((collection) => {
            const { Component, title, description, emptyMessage, module, query } = collection;
            return (
              <div key={title}>
                {createCollectionWrapper({
                  module,
                  query,
                  defaultTitle: title,
                  defaultDescription: description,
                  ClientComponent: Component,
                })({
                  title,
                  description,
                  emptyMessage,
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
